import { useEffect, useSyncExternalStore } from "react";

export type ThemePreference = "light" | "dark" | "system";

const STORAGE_KEY = "satchel_theme";
const SYSTEM_LIGHT = "(prefers-color-scheme: light)";
const listeners = new Set<() => void>();

export function getThemePreference(): ThemePreference {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === "light" || stored === "dark" ? stored : "system";
}

export function setThemePreference(preference: ThemePreference): void {
  if (preference === "system") {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, preference);
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(subscribe, getThemePreference);
}

/** Keeps the root element's `light` class in step with the preference and, for "system", the OS. */
export function useAppliedTheme(): void {
  const preference = useThemePreference();
  useEffect(() => {
    const media = window.matchMedia(SYSTEM_LIGHT);
    const apply = () => {
      const light = preference === "light" || (preference === "system" && media.matches);
      document.documentElement.classList.toggle("light", light);
    };
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [preference]);
}
